import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PaperPlaneTilt, CheckCircle, ShieldCheck, Phone, EnvelopeSimple, MapPin } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { trackLeadConversion } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "PC Tune-Up",
  "Malware & Virus Removal",
  "Data Backup & Recovery",
  "Remote Support",
  "Network & Wi-Fi Setup",
  "Hardware Repair & Upgrades",
  "Business IT Support",
  "MSP — Essentials Plan",
  "MSP — Professional Plan",
  "MSP — Enterprise Plan",
  "Not sure yet",
];

const initial = { firstName: "", lastName: "", email: "", phone: "", service: "", message: "" };

export default function ContactForm({ business, promoCode, onPromoClear }) {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => {
    const val = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: val }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.email || !form.phone || !form.service) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      trackLeadConversion({ event_source: "contact_form", service: form.service });
      toast.success("Thanks! We'll be in touch within the hour on business days.");
      setSubmitted(true);
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-10 lg:gap-16">
          {/* Left */}
          <div className="lg:col-span-2">
            <div className="text-sm font-semibold uppercase tracking-widest text-blue-600">Get in touch</div>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900">
              Free diagnostic. Free quote. Zero pressure.
            </h2>
            <p className="mt-4 text-lg text-zinc-600 leading-relaxed">
              Tell us what's going on and we'll reply within the hour on business days — or call us directly.
            </p>

            <div className="mt-8 space-y-4">
              <a href={`tel:${business?.phoneTel || "+19197375442"}`} className="flex items-center gap-3 group" data-testid="contact-phone">
                <div className="h-11 w-11 rounded-lg bg-blue-600/10 text-blue-600 grid place-items-center">
                  <Phone size={20} weight="duotone" />
                </div>
                <div>
                  <div className="text-xs text-zinc-500">Call us</div>
                  <div className="font-semibold text-zinc-900 group-hover:text-blue-600 transition-colors">{business?.phone}</div>
                </div>
              </a>
              <a href={`mailto:${business?.email}`} className="flex items-center gap-3 group" data-testid="contact-email">
                <div className="h-11 w-11 rounded-lg bg-blue-600/10 text-blue-600 grid place-items-center">
                  <EnvelopeSimple size={20} weight="duotone" />
                </div>
                <div>
                  <div className="text-xs text-zinc-500">Email</div>
                  <div className="font-semibold text-zinc-900 group-hover:text-blue-600 transition-colors">{business?.email}</div>
                </div>
              </a>
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-lg bg-blue-600/10 text-blue-600 grid place-items-center">
                  <MapPin size={20} weight="duotone" />
                </div>
                <div>
                  <div className="text-xs text-zinc-500">Based in</div>
                  <div className="font-semibold text-zinc-900">{business?.city}</div>
                </div>
              </div>
            </div>

            <div className="mt-8 flex items-center gap-2 text-sm text-zinc-500">
              <ShieldCheck size={16} weight="duotone" className="text-blue-600" />
              Your info is never shared. 100% confidential.
            </div>
          </div>

          {/* Right - form */}
          <div className="lg:col-span-3">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="bg-zinc-50 rounded-2xl p-6 sm:p-8 border border-zinc-200"
            >
              {submitted ? (
                <div data-testid="contact-success" className="py-12 text-center">
                  <CheckCircle size={48} weight="duotone" className="mx-auto text-blue-600" />
                  <h3 className="mt-4 font-heading text-2xl font-bold text-zinc-900">Message received.</h3>
                  <p className="mt-2 text-zinc-600">We'll be in touch shortly. For urgent issues, call us at <span className="font-semibold text-zinc-900">{business?.phone}</span>.</p>
                  <button type="button" onClick={() => setSubmitted(false)} className="mt-6 text-blue-600 font-semibold hover:underline" data-testid="contact-send-another">
                    Send another message
                  </button>
                </div>
              ) : (
                <>
                  {form.promo && (
                    <div data-testid="contact-promo-applied" className="mb-5 flex items-center justify-between gap-3 rounded-lg border-2 border-dashed border-blue-300 bg-blue-50 px-4 py-3">
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle size={16} weight="fill" className="text-blue-600" />
                        <span className="text-zinc-700">Promo applied:</span>
                        <span className="font-mono font-bold tracking-widest text-blue-700">{form.promo}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => { setForm((f) => ({ ...f, promo: "" })); onPromoClear && onPromoClear(); }}
                        className="text-xs text-zinc-500 hover:text-zinc-900"
                        data-testid="contact-promo-remove"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="firstName" className="text-zinc-700">First name *</Label>
                      <Input
                        id="firstName" data-testid="form-firstName" required
                        value={form.firstName} onChange={update("firstName")}
                        placeholder="Jane" className="mt-1.5 bg-white"
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastName" className="text-zinc-700">Last name</Label>
                      <Input
                        id="lastName" data-testid="form-lastName"
                        value={form.lastName} onChange={update("lastName")}
                        placeholder="Smith" className="mt-1.5 bg-white"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4 mt-4">
                    <div>
                      <Label htmlFor="email" className="text-zinc-700">Email *</Label>
                      <Input
                        id="email" type="email" data-testid="form-email" required
                        value={form.email} onChange={update("email")}
                        placeholder="you@example.com" className="mt-1.5 bg-white"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-zinc-700">Phone *</Label>
                      <Input
                        id="phone" type="tel" data-testid="form-phone" required
                        value={form.phone} onChange={update("phone")}
                        placeholder="(555) 123-4567" className="mt-1.5 bg-white"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <Label htmlFor="service" className="text-zinc-700">Service needed *</Label>
                    <Select value={form.service} onValueChange={update("service")}>
                      <SelectTrigger id="service" data-testid="form-service" className="mt-1.5 bg-white">
                        <SelectValue placeholder="Choose a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICES.map((s) => (
                          <SelectItem key={s} value={s} data-testid={`form-service-opt-${s.toLowerCase().replace(/\s+/g, "-")}`}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="mt-4">
                    <Label htmlFor="message" className="text-zinc-700">Describe the issue (optional)</Label>
                    <Textarea
                      id="message" data-testid="form-message" rows={4}
                      value={form.message} onChange={update("message")}
                      placeholder="E.g. My laptop is extremely slow after the last Windows update…"
                      className="mt-1.5 bg-white resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="form-submit"
                    className="mt-6 w-full bg-blue-600 hover:bg-blue-700 text-white h-12 rounded-full font-semibold blue-glow"
                  >
                    {submitting ? "Sending…" : (<span className="inline-flex items-center gap-2">Request free diagnostic <PaperPlaneTilt size={18} weight="fill" /></span>)}
                  </Button>

                  <p className="mt-4 text-xs text-zinc-500 text-center">
                    By submitting, you agree to be contacted about your request. No spam — ever.
                  </p>
                </>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
